import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Loader2, Save, X, ShieldCheck, Ban, Mail, User as UserIcon, StickyNote } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';

// Admin editor for any user.
// Props: userId, open, onOpenChange, onSaved()
const AdminEditUserDialog = ({ userId, open, onOpenChange, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    api.get(`/admin/users/${userId}/full`).then((r) => {
      const u = r.data || {};
      setForm({
        name: u.name || '',
        last_name: u.last_name || '',
        email: u.email || '',
        is_admin: !!u.is_admin,
        blocked: !!u.blocked,
        accepts_marketing: !!u.accepts_marketing,
        notes: u.notes || '',
      });
    }).catch(() => toast({ title: 'No se pudo cargar el usuario' }))
      .finally(() => setLoading(false));
  }, [open, userId]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim() || null,
        last_name: form.last_name.trim() || null,
        email: form.email.trim() || null,
        is_admin: form.is_admin,
        blocked: form.blocked,
        accepts_marketing: form.accepts_marketing,
        notes: form.notes.trim() || null,
      };
      await api.patch(`/admin/users/${userId}`, payload);
      toast({ title: 'Usuario actualizado' });
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'No se pudo guardar.' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-neutral-950 border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="admin-edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <UserIcon className="w-4 h-4 text-orange-400" /> Editar usuario
          </DialogTitle>
        </DialogHeader>

        {loading || !form ? (
          <div className="py-12 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>
        ) : (
          <div className="space-y-5">
            {/* Datos personales */}
            <section className="space-y-3">
              <p className="text-[11px] uppercase tracking-widest font-black text-orange-400 flex items-center gap-2">
                <UserIcon className="w-3.5 h-3.5" /> Datos personales
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-white/60">Nombre</label>
                  <Input value={form.name} onChange={(e) => set({ name: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="admin-user-name" />
                </div>
                <div>
                  <label className="text-xs text-white/60">Apellido</label>
                  <Input value={form.last_name} onChange={(e) => set({ last_name: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="admin-user-last-name" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-white/60 flex items-center gap-1"><Mail className="w-3 h-3" /> Correo</label>
                  <Input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="admin-user-email" />
                </div>
              </div>
            </section>

            {/* Rol & seguridad */}
            <section className="space-y-3">
              <p className="text-[11px] uppercase tracking-widest font-black text-orange-400 flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5" /> Rol y seguridad
              </p>
              <div className="grid grid-cols-3 gap-3">
                <label className={`cursor-pointer rounded-xl border p-3 flex flex-col items-start gap-1 transition-colors ${form.is_admin ? 'border-orange-500/50 bg-orange-500/10 text-orange-300' : 'border-white/10 bg-white/[0.02] text-white/70 hover:bg-white/[0.04]'}`}>
                  <div className="flex items-center gap-2 w-full">
                    <input type="checkbox" checked={form.is_admin} onChange={(e) => set({ is_admin: e.target.checked })} className="accent-orange-500" data-testid="admin-user-is-admin" />
                    <span className="font-bold text-sm">Administrador</span>
                  </div>
                  <span className="text-[10px] leading-snug opacity-80">Acceso total al panel /admin</span>
                </label>
                <label className={`cursor-pointer rounded-xl border p-3 flex flex-col items-start gap-1 transition-colors ${form.blocked ? 'border-rose-500/50 bg-rose-500/10 text-rose-300' : 'border-white/10 bg-white/[0.02] text-white/70 hover:bg-white/[0.04]'}`}>
                  <div className="flex items-center gap-2 w-full">
                    <input type="checkbox" checked={form.blocked} onChange={(e) => set({ blocked: e.target.checked })} className="accent-rose-500" data-testid="admin-user-blocked" />
                    <span className="font-bold text-sm flex items-center gap-1"><Ban className="w-3.5 h-3.5" /> Bloqueado</span>
                  </div>
                  <span className="text-[10px] leading-snug opacity-80">No podrá iniciar sesión ni comprar</span>
                </label>
                <label className={`cursor-pointer rounded-xl border p-3 flex flex-col items-start gap-1 transition-colors ${form.accepts_marketing ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' : 'border-white/10 bg-white/[0.02] text-white/70 hover:bg-white/[0.04]'}`}>
                  <div className="flex items-center gap-2 w-full">
                    <input type="checkbox" checked={form.accepts_marketing} onChange={(e) => set({ accepts_marketing: e.target.checked })} className="accent-emerald-500" data-testid="admin-user-marketing" />
                    <span className="font-bold text-sm">Marketing OK</span>
                  </div>
                  <span className="text-[10px] leading-snug opacity-80">Acepta correos promocionales</span>
                </label>
              </div>
            </section>

            {/* Notas internas */}
            <section className="space-y-2">
              <p className="text-[11px] uppercase tracking-widest font-black text-orange-400 flex items-center gap-2">
                <StickyNote className="w-3.5 h-3.5" /> Notas internas (sólo admins)
              </p>
              <Textarea value={form.notes} onChange={(e) => set({ notes: e.target.value })} rows={3} placeholder="Cliente VIP, preferencias, referencias, etc." className="bg-white/5 border-white/10 text-white resize-none" data-testid="admin-user-notes" />
            </section>

            <div className="flex justify-end gap-2 pt-3 border-t border-white/10">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} className="bg-transparent border-white/20 text-white hover:bg-white/10">
                <X className="w-4 h-4 mr-1" /> Cancelar
              </Button>
              <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-black font-bold" data-testid="admin-user-save">
                {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Guardando...</> : <><Save className="w-4 h-4 mr-1" /> Guardar cambios</>}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdminEditUserDialog;
